import { emptyStateGenericGraphic } from '@workday/canvas-graphics-web';
import { Graphic } from '@workday/canvas-kit-react/icon';
import { useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';
import { useParams } from 'react-router-dom-v5-compat';

import PageTitle from '~/components/Shared/PageTitle/PageTitle';
import { Box } from '~/eds/src/components/Box/Box';
import { Button } from '~/eds/src/components/Button/Button';
import { EmptyPage } from '~/eds/src/components/EmptyPage/EmptyPage';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { LoadingSpinner } from '~/eds/src/components/LoadingSpinner/LoadingSpinner';
import { SearchInput } from '~/eds/src/components/SearchInput/SearchInput';
import { Text } from '~/eds/src/components/Text/Text';
import { Tree } from '~/eds/src/components/Tree/Tree';
import { useDebouncedState } from '~/eds/src/hooks/useDebouncedState';
import { TreeNode } from '~/eds/src/types/TreeNode';
import { Details } from '~/features/guidelines/details/Details';
import { Form } from '~/features/guidelines/form/Form';
import { getVariationLevelLabel } from '~/features/playbooks/utils';
import { usePermission } from '~/hooks/usePermission';
import { api } from '~/redux/api/createApi';
import { GuidelineWithId } from '~/redux/api/types/playbooks/guidelines';
import { useRouting } from '~/routing/useRouting';
import { Nullable } from '~/types/Nullable';

const clauseLibraryEditPermission = {
  resourceId: 'clause_library',
  resourceType: 'edit',
};

const Page = () => {
  const intl = useIntl();
  const { navigate } = useRouting();
  const [debouncedSearch, search, setSearch] =
    useDebouncedState<Nullable<string>>('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedGuideline, setSelectedGuideline] = useState<
    GuidelineWithId | undefined
  >(undefined);
  const [expandedGuidelineIds, setExpandedGuidelineIds] = useState<Set<string>>(
    () => new Set(),
  );
  const { guidelineId } = useParams<{ guidelineId: string }>();

  const {
    data: guidelinesResponse = { data: [] },
    isFetching: isFetchingGuidelines,
  } = api.endpoints.getGuidelines.useQuery(debouncedSearch ?? '');

  const { isLoading: isLoadingPermissions, hasPermission: hasEditPermission } =
    usePermission({
      permission: clauseLibraryEditPermission,
    });

  useEffect(() => {
    if (guidelineId) {
      setExpandedGuidelineIds((prev) => new Set(prev).add(guidelineId));
    }
  }, [guidelineId]);

  const guidelinesTree = useMemo(() => {
    return guidelinesResponse.data.map((guideline) => ({
      key: guideline.attributes.uuid,
      name: guideline.attributes.name,
      isExpanded: expandedGuidelineIds.has(guideline.attributes.uuid),
      onClick: () => {
        navigate(`/guidelines/${guideline.attributes.uuid}`);
      },
      children: [
        ...((guideline.attributes.variations ?? []).map((variation) => ({
          key: variation.clause_variation_id,
          name: variation.name,
          children: [],
          parentKey: guideline.attributes.uuid,
          status: {
            icon: 'status-current' as const,
            tooltip: variation.position,
            color:
              variation.position === 'Preferred'
                ? ('status.success' as const)
                : variation.position === 'Fallback'
                  ? ('status.warning' as const)
                  : ('status.danger' as const),
            label: getVariationLevelLabel(variation.position, intl),
          },
        })) ?? []),
      ],
    }));
  }, [guidelinesResponse.data, expandedGuidelineIds]);

  const handleTreeUpdate = (updatedNodes: TreeNode[]) => {
    const updatedExpanded = new Set<string>();
    for (const node of updatedNodes) {
      if (node.isExpanded) updatedExpanded.add(node.key);
    }
    setExpandedGuidelineIds((prev) => {
      if (
        prev.size !== updatedExpanded.size ||
        [...updatedExpanded].some((id) => !prev.has(id))
      ) {
        return updatedExpanded;
      }
      return prev;
    });
  };

  return (
    <Layout direction="column" h="calc(100vh - 144px)">
      <Layout
        direction="column"
        spacing={8}
        bg="white"
        borderBottom="border.divider"
        pb={4}
      >
        <Layout justify="space-between" align="center">
          <PageTitle
            title={intl.formatMessage({
              id: 'guidelines.pageTitle',
              defaultMessage: 'Guidelines',
              description: 'Page title for Guidelines',
            })}
          />
          <Button
            variant="primary"
            text={intl.formatMessage({
              id: 'guidelines.createGuidelineButton',
              defaultMessage: 'Create Guideline',
              description: 'Button text to create a new guideline',
            })}
            isLoading={isLoadingPermissions}
            disabled={!hasEditPermission}
            onClick={() => setIsFormOpen(true)}
          />
        </Layout>
        <Box w="50%">
          <SearchInput name="search" value={search} onChange={setSearch} />
        </Box>
      </Layout>

      <Layout direction="row" flex="1" minH={0}>
        <Layout
          direction="column"
          flex="1"
          minW="sidebar.width.s"
          maxW="sidebar.width.m"
          borderRight="border.divider"
        >
          {isFetchingGuidelines ? (
            <>
              <Layout
                as="header"
                align="center"
                justify="space-between"
                styles={{
                  height: 'header.height',
                  borderBottom: 'border.divider',
                  flex: 'none',
                  paddingLeft: 6,
                  paddingRight: 4,
                }}
              >
                <Text variant="body-bold" fontSize="16">
                  {intl.formatMessage({
                    id: 'guidelines.pageTitle',
                    defaultMessage: 'Guidelines',
                    description: 'Page title for Guidelines',
                  })}
                </Text>
              </Layout>
              <Layout flex="1" align="center" justify="center">
                <LoadingSpinner />
              </Layout>
            </>
          ) : (
            <>
              <Layout
                borderBottom="border.divider"
                minH="header.height"
                justify="space-between"
                align="center"
                bg="white"
                px={8}
              >
                {/* eslint-disable-next-line jsx-a11y/aria-role -- TODO: EKP-42656 */}
                <Text variant="body-bold" fontSize="l" role="subheading">
                  {intl.formatMessage({
                    id: 'guidelines.pageTitle',
                    defaultMessage: 'Guidelines',
                    description: 'Page title for Guidelines',
                  })}
                </Text>
                <Text preset="help">{guidelinesResponse?.data?.length}</Text>
              </Layout>

              <Layout p={2} w="100%" overflowY="scroll">
                <Tree
                  expandSelectedOnSelect={false}
                  selectedNodeKey={guidelineId}
                  tree={guidelinesTree}
                  onUpdate={handleTreeUpdate}
                />
              </Layout>
            </>
          )}
        </Layout>

        <Layout flex="4" direction="column">
          {guidelineId ? (
            <Details
              hasEditPermission={hasEditPermission}
              onEdit={(guideline) => {
                setIsFormOpen(true);
                setSelectedGuideline(guideline);
              }}
              guidelineId={guidelineId}
            />
          ) : (
            <Layout direction="column" flex="1" align="center" justify="center">
              <Layout direction="column">
                <Graphic src={emptyStateGenericGraphic} />
                <EmptyPage preset="no-guideline-selected" />
              </Layout>
            </Layout>
          )}
        </Layout>
      </Layout>
      {isFormOpen && (
        <Form
          guideline={selectedGuideline}
          onClose={() => {
            setIsFormOpen(false);
            setSelectedGuideline(undefined);
          }}
        />
      )}
    </Layout>
  );
};

export default Page;
